import { useSelector } from 'react-redux'
import type { Constant } from '@pbt/pbt-ui-components'

import { PrescriptionType } from '~/constants/prescription'
import i18n from '~/locales/i18n'
import {
  getAutoshipUnit,
  getPrescriptionProductSizeUnit,
  getPrescriptionStates,
  getProductSizeUnit,
} from '~/store/reducers/constants'
import type { Prescription } from '~/types/entities/prescriptions'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import {
  computeRelativeTime,
  type DisplayField,
  getInstructionsField,
  getOrderTableFields,
  getOrderTableSubHeader,
  getPrescriptionFlags,
  getPrescriptionStateName,
  getRxFields,
  type OrderTableContent,
  shouldSuppressFields,
} from './rxCardUtilities'

interface UseRxContentOptions {
  itemDate?: string
  onDispenseRefill?: () => void
}

export interface RxContent {
  fields: DisplayField[]
  instructions: DisplayField
  isCustomView: boolean
  orderTableData: OrderTableContent | null
  orderTableSubHeader: string | null
  orderTableTitle: string
  prescriptionStateName: string | null
  showTime: string | null
}

/**
 * Hook that fetches Redux data and returns Rx fields and instructions for the card.
 */
export const useRxCardContent = (
  prescription: Prescription,
  options?: UseRxContentOptions,
): RxContent => {
  const { itemDate, onDispenseRefill } = options ?? {}

  const prescriptionStates = useSelector(getPrescriptionStates) as
    | Constant[]
    | undefined
  const prescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  ) as Constant[] | undefined
  const productSizeUnit = useSelector(getProductSizeUnit) as
    | Constant[]
    | undefined
  const autoshipUnit = useSelector(getAutoshipUnit) as Constant[] | undefined

  const { prescriptionType } = prescription

  const flags = getPrescriptionFlags(
    prescription,
    prescriptionType,
    prescriptionStates,
  )

  const shouldEnableDispenseRefill =
    prescriptionType === PrescriptionType.IN_HOUSE && !flags.isCanceled

  const fields = getRxFields(prescription, prescriptionType, {
    prescriptionStates,
    prescriptionProductSizeUnit,
    productSizeUnit,
    autoshipUnit,
    onDispenseRefill: shouldEnableDispenseRefill ? onDispenseRefill : undefined,
  })

  const instructions = getInstructionsField(prescription)
  const prescriptionStateName = getPrescriptionStateName(
    prescription,
    prescriptionStates,
  )

  const isCustomView = shouldSuppressFields(flags)

  const timezone = useCurrentBusinessTimezone()
  const showTime =
    isCustomView && itemDate ? computeRelativeTime(itemDate, timezone) : null

  const orderTableTitle = i18n.t('Common:ORDERS')
  const orderTableSubHeader = getOrderTableSubHeader(
    prescription,
    prescriptionType,
  )

  const orderTableData = isCustomView
    ? null
    : getOrderTableFields(prescription, prescriptionType, {
        autoshipUnit,
        prescriptionProductSizeUnit,
        productSizeUnit,
        prescriptionStates,
        timezone,
      })

  return {
    fields,
    instructions,
    isCustomView,
    orderTableData,
    orderTableSubHeader,
    orderTableTitle,
    prescriptionStateName,
    showTime,
  }
}
